/**
 * ProShip Direct — L1 serviceability for the merchant's own courier.
 *
 * WHAT L1 IS. Three pincode lists per courier account: where the carrier collects, where it
 * delivers prepaid, where it delivers COD. Allocation reads them before it offers the courier a
 * shipment. They live in one `bulk_serviceability` document, scoped to this merchant alone.
 *
 * TWO WAYS TO FILL THEM, and the choice is the screen. Inherit ProShip's coverage for this
 * carrier and service — a copy, so it is theirs to edit afterwards and immune to a later change
 * on Prozo's side — or upload their own single-column files. Inheriting is offered only when
 * ProShip actually holds coverage for that carrier and service; where it does not, the option is
 * absent rather than disabled, because an inheritance that delivers an empty map would leave a
 * courier that looks configured and ships nowhere.
 *
 * REPLACED A FIXTURE PAGE that parsed a two-column lane file into browser storage. Lanes are a
 * different layer (pd_serviceability, for same-day and next-day services) and are not built yet;
 * this page is L1 only and says so.
 */
import React, { useState } from 'react';
import { Box, Grid, Typography, Button, Alert, Chip, Divider, CircularProgress } from '@mui/material';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';

import withLayout from '../../../hocs/Layout';
import SetupPageShell from '../components/SetupPageShell';
import FileDropZone from '../components/FileDropZone';
import Loader from '../../../common/Loader';
import { sectionCardSx, sectionTitleSx, primaryBtnSx } from '../../../styles/patterns';
import { statCardSx, statLabelSx, statValueSx } from '../components/stats';
import { SUBTLE, NAVY, SUCCESS, BORDER, WARNING, PRIMARY_BLUE } from '../../../styles/foundation/colors';
import { FONT, SIZE, WEIGHT } from '../../../styles/foundation/typography';
import { GAP, RADIUS } from '../../../styles/foundation/spacing';
import { MONO } from '../components/directCellStyles';
import { SETUP_DIMENSIONS } from '../data/defaults';
import { downloadCsv } from '../data/serviceabilityUpload';
import { useCourierServiceability } from '../data/hooks';
import { errText, fetchCourierServiceabilityList } from '../data/api';
import { formatDate } from '../../../helpers/UtilityHelper';

const dimension = SETUP_DIMENSIONS.find((d) => d.key === 'serviceability');

const bodySx = { fontFamily: FONT, fontSize: SIZE['2xs'], color: SUBTLE, lineHeight: 1.6 };
const smallSx = { fontFamily: FONT, fontSize: SIZE['3xs'], color: SUBTLE, lineHeight: 1.5 };
const strongSx = { fontFamily: FONT, fontSize: SIZE['2xs'], fontWeight: WEIGHT.semibold, color: NAVY };
const linkSx = { fontFamily: FONT, fontSize: SIZE['3xs'], color: PRIMARY_BLUE, cursor: 'pointer', textDecoration: 'underline' };

/**
 * The three lists, in the order a merchant thinks about them: collect, then deliver, then the
 * COD subset of deliver. `key` is the multipart part name the server expects.
 */
const LISTS = [
  {
    key: 'pickupPrepaid',
    title: 'Pickup pincodes',
    blurb: 'Where this courier will collect from you.',
    countField: 'pickupPrepaidCount',
  },
  {
    key: 'dropPrepaid',
    title: 'Delivery pincodes — prepaid',
    blurb: 'Where it delivers a prepaid shipment.',
    countField: 'dropPrepaidCount',
  },
  {
    key: 'dropCod',
    title: 'Delivery pincodes — COD',
    blurb: 'Where it delivers cash on delivery. Often narrower than prepaid.',
    countField: 'dropCodCount',
  },
];

/** One column, one pincode per row, with a header the parser ignores. */
const TEMPLATE = 'pincode\n110001\n400001\n560001\n';

const nfmt = (n) => Number(n || 0).toLocaleString('en-IN');

function Coverage({ account }) {
  const { status, loading, error, upload, inherit } = useCourierServiceability(account?.accountCode);
  /* Files chosen but not yet sent — nothing reaches the server until Save. */
  const [staged, setStaged] = useState({});
  const [busy, setBusy] = useState(false);
  const [problem, setProblem] = useState(null);
  const [done, setDone] = useState(null);
  const [fetchingList, setFetchingList] = useState(null);

  if (loading) return <Loader />;

  const stagedCount = Object.values(staged).filter(Boolean).length;
  const hasAny = status
    && (status.pickupPrepaidCount || status.dropPrepaidCount || status.dropCodCount);

  /* One column, header `pincode` — the same shape the upload reads, so it can go straight back up. */
  const downloadList = async (l) => {
    setFetchingList(l.key);
    setProblem(null);
    try {
      const res = await fetchCourierServiceabilityList(account.accountCode, l.key);
      const pins = res?.data?.pincodes || [];
      downloadCsv(`${account.accountCode}-${l.key}.csv`, `pincode\n${pins.join('\n')}\n`);
    } catch (e) {
      setProblem(errText(e));
    } finally {
      setFetchingList(null);
    }
  };

  const run = async (fn, okMessage) => {
    setBusy(true);
    setProblem(null);
    setDone(null);
    try {
      await fn();
      setStaged({});
      setDone(okMessage);
    } catch (e) {
      setProblem(e.message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <>
      {error && (
        <Alert severity='warning' sx={{ mb: 1.5, fontFamily: FONT, fontSize: SIZE['2xs'] }}>{error}</Alert>
      )}

      {/*
        The outcome of the last action, at the top: it is about the courier's coverage as a whole,
        and "now uses ProShip's coverage" read oddly sitting under the upload drop zones beside a
        disabled Save button.
      */}
      {done && (
        <Alert severity='success' sx={{ mb: 1.5, fontFamily: FONT, fontSize: SIZE['2xs'] }}>{done}</Alert>
      )}

      {/* ── Where this account stands ── */}
      <Box sx={{ ...sectionCardSx, mb: 1.5 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: GAP.sm, flexWrap: 'wrap', mb: 1 }}>
          <Typography sx={sectionTitleSx}>Coverage</Typography>
          {status?.source && (
            <Chip
              size='small'
              label={status.source === 'OWN' ? 'Yours' : 'ProShip’s'}
              sx={{
                fontFamily: FONT, fontSize: SIZE['4xs'], fontWeight: WEIGHT.semibold, height: 20,
                bgcolor: `${SUCCESS}14`, color: SUCCESS,
              }}
            />
          )}
        </Box>

        <Grid container spacing={1}>
          {LISTS.map((l) => (
            <Grid item xs={12} md={4} key={l.key}>
              <Box sx={statCardSx}>
                <Typography sx={statLabelSx}>{l.title}</Typography>
                <Typography sx={statValueSx}>{nfmt(status?.[l.countField])}</Typography>
                <Typography sx={smallSx}>{l.blurb}</Typography>
              </Box>
            </Grid>
          ))}
        </Grid>

        {status?.updatedAt && (
          <Typography sx={{ ...smallSx, mt: 1 }}>
            Last updated {formatDate(status.updatedAt, 'dd MMM yyyy, hh:mm a')}
            {status.updatedBy ? ` by ${status.updatedBy}` : ''}
            {status.inheritedFrom ? ` · reading ${status.inheritedFrom}` : ''}
          </Typography>
        )}

        {!hasAny && (
          <Alert severity='warning' sx={{ mt: 1.25, fontFamily: FONT, fontSize: SIZE['2xs'] }}>
            This courier has no coverage yet, so allocation will not use it. Inherit ProShip’s or
            upload your own below.
          </Alert>
        )}
      </Box>

      {/* ── Inherit, when there is something to inherit ── */}
      {status?.canInherit && (
        <Box sx={{ ...sectionCardSx, mb: 1.5 }}>
          <Typography sx={{ ...sectionTitleSx, mb: 0.5 }}>Use ProShip’s coverage</Typography>
          {status.source === 'PROSHIP_DEFAULT' ? (
            <Typography sx={{ ...bodySx, mb: 1.25 }}>
              This courier is running on ProShip’s coverage for {account.carrierName} on this
              service. Nothing was copied — it reads ProShip’s lists directly, so whenever ProShip
              widens coverage for this carrier you get it without doing anything. Upload your own
              files below to take over.
            </Typography>
          ) : (
            <Typography sx={{ ...bodySx, mb: 1.25 }}>
              Runs this courier on ProShip’s pincode lists for {account.carrierName} on this
              service. Nothing is copied and nothing is frozen — your courier reads ProShip’s
              lists, so it stays current as ProShip’s coverage changes. You can upload your own
              files later to take over.
            </Typography>
          )}
          {status.source !== 'PROSHIP_DEFAULT' && (
            <Button
              size='small'
              variant='contained'
              sx={primaryBtnSx}
              disabled={busy}
              startIcon={busy ? <CircularProgress size={13} /> : <CheckCircleIcon sx={{ fontSize: 16 }} />}
              onClick={() => run(inherit, 'This courier now uses ProShip’s coverage.')}
            >
              Use ProShip’s coverage
            </Button>
          )}
        </Box>
      )}

      {/* ── Or upload ── */}
      <Box sx={sectionCardSx}>
        <Typography sx={{ ...sectionTitleSx, mb: 0.5 }}>Upload your own</Typography>
        <Typography sx={{ ...bodySx, mb: 1.5 }}>
          One column, one six-digit pincode per row — the same file your carrier gives you.
          Attach only the lists you want to change: anything you leave out stays as it is.
          Applies to {account.carrierName} · <Box component='span' sx={{ fontFamily: MONO }}>{account.accountCode}</Box> and
          nothing else.
          {status?.source === 'PROSHIP_DEFAULT' && ' Uploading takes this courier off ProShip’s '
            + 'coverage — any list you don’t attach is carried over from it as it stands today, '
            + 'and stops tracking ProShip’s changes.'}
        </Typography>

        <Grid container spacing={1.5}>
          {LISTS.map((l) => (
            <Grid item xs={12} md={4} key={l.key}>
              {/*
                Title and links on two fixed lines, not one wrapping row: a long title ("Delivery
                pincodes — prepaid") pushed its links onto a second line while the short one kept them
                inline, so the three drop zones started at different heights.
              */}
              <Typography sx={{ ...strongSx, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                {l.title}
              </Typography>
              <Box sx={{ display: 'flex', alignItems: 'baseline', gap: GAP.md, mt: 0.25, mb: 0.75, minHeight: 18 }}>
                {/*
                  Both, beside the drop zone they feed. The current list is the useful one: someone on
                  ProShip's coverage who wants their own usually starts from ProShip's — download, edit,
                  upload — and the server returns ProShip's list when that is what the courier reads.
                */}
                <Typography
                  onClick={() => downloadCsv(`${l.key}-template.csv`, TEMPLATE)}
                  sx={linkSx}
                >
                  Template
                </Typography>
                {status?.[l.countField] > 0 && (
                  <Typography
                    onClick={() => !fetchingList && downloadList(l)}
                    sx={{ ...linkSx, opacity: fetchingList === l.key ? 0.5 : 1 }}
                  >
                    {fetchingList === l.key
                      ? 'Preparing…'
                      : `${status.source === 'PROSHIP_DEFAULT' ? 'ProShip’s' : 'Current'} list (${nfmt(status[l.countField])})`}
                  </Typography>
                )}
              </Box>
              {staged[l.key] ? (
                <Box sx={{ border: `1px solid ${SUCCESS}`, borderRadius: RADIUS.md, p: 1.25 }}>
                  <Typography sx={strongSx}>{staged[l.key].name}</Typography>
                  <Typography
                    onClick={() => setStaged((p) => ({ ...p, [l.key]: null }))}
                    sx={{ ...smallSx, color: WARNING, cursor: 'pointer', textDecoration: 'underline', mt: 0.4 }}
                  >
                    Remove
                  </Typography>
                </Box>
              ) : (
                <FileDropZone
                  onFile={(f) => { setDone(null); setProblem(null); setStaged((p) => ({ ...p, [l.key]: f })); }}
                  /* Same text in all three so the boxes are the same height — the title above says which list. */
                  hint='Replaces this list on the account.'
                />
              )}
            </Grid>
          ))}
        </Grid>

        {problem && (
          <Alert severity='error' sx={{ mt: 1.5, fontFamily: FONT, fontSize: SIZE['2xs'] }}>{problem}</Alert>
        )}
        <Divider sx={{ my: 1.5, borderColor: BORDER }} />
        <Box sx={{ display: 'flex', alignItems: 'center', gap: GAP.md, flexWrap: 'wrap' }}>
          <Button
            size='small'
            variant='contained'
            sx={primaryBtnSx}
            disabled={busy || stagedCount === 0}
            startIcon={busy ? <CircularProgress size={13} /> : null}
            onClick={() => run(
              () => upload(staged),
              `${stagedCount} list${stagedCount === 1 ? '' : 's'} uploaded.`
            )}
          >
            {busy ? 'Uploading…' : `Save ${stagedCount || ''} file${stagedCount === 1 ? '' : 's'}`.trim()}
          </Button>
          {stagedCount === 0 && <Typography sx={smallSx}>Attach at least one file.</Typography>}
        </Box>
      </Box>
    </>
  );
}

export function Serviceability({ embedded = false }) {
  return (
    <SetupPageShell dimension={dimension} embedded={embedded} serverManaged>
      {({ account }) => <Coverage account={account} />}
    </SetupPageShell>
  );
}

export default withLayout(Serviceability);
